import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import AutoSuggest from "react-autosuggest"
import Fuse from "fuse.js"
import { Link, navigate } from "gatsby"
import { useIntl } from "react-intl"
import {
  useAllBlogs,
  useAllCareers,
  useAllCompanies,
  useAllMajors,
  useAllSchools,
  useAllStories,
} from "../../data/graphql"
import { FirebaseContext } from "../Firebase"
import { globalSearchQuickLinks } from "../../data/search/globalSearchQuickLinks"
import useWorkReadinessSearchItems from "../../data/useWorkReadinessSearchItems"
import "../algolia/algolia.css"
import "../algolia/algolia-explore.css"

const MIN_QUERY_LENGTH = 2
const SECTION_LIMIT = 10
const SUGGESTION_DEBOUNCE_MS = 180
const QUICK_LINKS_SECTION = "quick-links"
const WORK_READINESS_CONTENT_SECTION = "work-readiness-content"

const SECTION_CONFIG = {
  stories: {
    sectionLabelEn: "stories",
    sectionLabelId: "auto-complete-stories",
    sectionLabelDefault: "stories",
    path: "/story/",
    keys: [
      { name: "story_job_title", weight: 0.05 },
      { name: "title", weight: 0.22 },
      { name: "story_keywords_text", weight: 0.17 },
      { name: "story_career_synonyms_text", weight: 0.5 },
      { name: "story_school_reference_titles_text", weight: 0.5 },
      { name: "story_company", weight: 0.08 },
      { name: "story_location", weight: 0.04 },
    ],
  },
  careers: {
    sectionLabelEn: "careers",
    sectionLabelId: "details-alt-college-careers",
    sectionLabelDefault: "Careers",
    path: "/career/",
    keys: [
      { name: "title", weight: 0.5 },
      { name: "career_keywords_text", weight: 0.3 },
      { name: "career_job_categories_text", weight: 0.1 },
      { name: "career_pathways_text", weight: 0.075 },
      { name: "career_education_level", weight: 0.025 },
    ],
  },
  schools: {
    sectionLabelEn: "schools",
    sectionLabelId: "algolia-toggle-dropdown-schools",
    sectionLabelDefault: "Schools",
    path: "/school/",
    keys: [
      { name: "title", weight: 0.5 },
      { name: "school_keywords_text", weight: 0.3 },
      { name: "school_city", weight: 0.12 },
      { name: "school_state_or_region", weight: 0.08 },
    ],
  },
  majors: {
    sectionLabelEn: "majors",
    sectionLabelId: "algolia-toggle-dropdown-majors",
    sectionLabelDefault: "Majors",
    path: "/major/",
    keys: [
      { name: "title", weight: 0.5 },
      { name: "major_keywords_text", weight: 0.3 },
      { name: "major_job_categories_text", weight: 0.15 },
      { name: "major_pathways_text", weight: 0.05 },
    ],
  },
  companies: {
    sectionLabelEn: "companies",
    sectionLabelId: "algolia-toggle-dropdown-companies",
    sectionLabelDefault: "Featured Employers",
    path: "/company/",
    keys: [
      { name: "title", weight: 0.6 },
      { name: "company_location", weight: 0.25 },
      { name: "company_industry_text", weight: 0.15 },
    ],
  },
  blogs: {
    sectionLabelEn: "Posts",
    sectionLabelId: "auto-complete-posts",
    sectionLabelDefault: "Posts",
    path: "/news/",
    keys: [{ name: "title", weight: 1 }],
  },
  [QUICK_LINKS_SECTION]: {
    sectionLabelEn: "Quick Links",
    sectionLabelId: "global-search-section-quick-links",
    sectionLabelDefault: "Quick Links",
    keys: [
      { name: "title", weight: 0.6 },
      { name: "quick_link_aliases_text", weight: 0.35 },
      { name: "quick_link_category_text", weight: 0.05 },
    ],
  },
  [WORK_READINESS_CONTENT_SECTION]: {
    sectionLabelEn: "Modules and Programs",
    sectionLabelId: "global-search-section-work-readiness",
    sectionLabelDefault: "Modules and Programs",
    keys: [
      { name: "title", weight: 0.7 },
      { name: "work_readiness_aliases_text", weight: 0.2 },
      { name: "work_readiness_type_text", weight: 0.1 },
    ],
  },
}

const toCategoryText = (categories) => {
  if (Array.isArray(categories)) {
    return categories.map((item) => item?.title || item?.value || item).filter(Boolean).join(" ")
  }

  if (categories && typeof categories === "object") {
    return categories.title || categories.value || ""
  }

  return categories || ""
}

const normalizeText = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeText(item)).join(" ")
  }

  if (value && typeof value === "object") {
    return normalizeText(Object.values(value))
  }

  if (value === null || value === undefined) {
    return ""
  }

  return String(value).toLowerCase().trim()
}

const WORD_ROOT_MIN_LENGTH = 4
const WORD_ROOT_PREFIX_LENGTH = 5

const sharesWordRoot = (tokenWord, targetWord) => {
  if (!tokenWord || !targetWord) {
    return false
  }

  if (targetWord.includes(tokenWord) || tokenWord.includes(targetWord)) {
    return true
  }

  const minLength = Math.min(tokenWord.length, targetWord.length)
  if (minLength < WORD_ROOT_MIN_LENGTH) {
    return false
  }

  const prefixLength = Math.min(WORD_ROOT_PREFIX_LENGTH, minLength)
  return tokenWord.slice(0, prefixLength) === targetWord.slice(0, prefixLength)
}

const tokenMatchesText = (token, text) => {
  if (!token) {
    return true
  }

  if (!text) {
    return false
  }

  if (text.includes(token)) {
    return true
  }

  const words = text.split(/[^a-z0-9]+/).filter(Boolean)
  return words.some((word) => sharesWordRoot(token, word))
}

const getValueByPath = (item, keyPath) => {
  if (!item || !keyPath) {
    return ""
  }

  return keyPath.split(".").reduce((acc, key) => {
    if (acc === null || acc === undefined) {
      return ""
    }

    return acc[key]
  }, item)
}

const matchesQueryTokensForKeys = (item, query, keys) => {
  const normalizedQuery = normalizeText(query)
  if (!normalizedQuery) {
    return true
  }

  const tokens = normalizedQuery.split(/\s+/).filter(Boolean)
  if (!tokens.length) {
    return true
  }

  const searchableText = normalizeText((keys || []).map((keyConfig) => {
    const keyPath = typeof keyConfig === "string" ? keyConfig : keyConfig?.name
    return getValueByPath(item, keyPath)
  }))

  return tokens.every((token) => searchableText.includes(token))
}

const isExternalUrl = (value) => /^https?:\/\//i.test(String(value || ""))

const getTitleMatchPriority = (item, query) => {
  const normalizedQuery = normalizeText(query)
  if (!normalizedQuery) {
    return 4
  }

  const normalizedTitle = normalizeText(item?.title)
  if (!normalizedTitle) {
    return 4
  }

  if (normalizedTitle === normalizedQuery) {
    return 0
  }

  const titleWords = normalizedTitle.split(/[^a-z0-9]+/).filter(Boolean)
  if (titleWords.includes(normalizedQuery)) {
    return 1
  }

  if (normalizedTitle.startsWith(normalizedQuery)) {
    return 2
  }

  if (normalizedTitle.includes(normalizedQuery)) {
    return 3
  }

  return 4
}


const getStorySchoolReferenceTitles = (story) => {
  if (Array.isArray(story?.story_school_reference_titles)) {
    return story.story_school_reference_titles.filter(Boolean)
  }

  const schoolReferences = story?.story_school_reference
  if (!Array.isArray(schoolReferences)) {
    return []
  }

  return schoolReferences
    .map((schoolReference) => schoolReference?.title)
    .filter(Boolean)
}

const matchesStoryQueryTokens = (story, query) => {
  const normalizedQuery = normalizeText(query)
  if (!normalizedQuery) {
    return true
  }

  const tokens = normalizedQuery.split(/\s+/).filter(Boolean)
  if (!tokens.length) {
    return true
  }

  const searchableText = normalizeText([
    story?.story_searchable_text,
    story?.story_job_title,
    story?.title,
    story?.story_keywords_text,
    story?.story_company,
    story?.story_location,
    story?.story_career_synonyms_text,
    story?.story_school_reference_titles_text,
    getStorySchoolReferenceTitles(story),
  ])

  const matchedTokensCount = tokens.filter((token) => tokenMatchesText(token, searchableText)).length
  const minimumMatches = tokens.length <= 2 ? tokens.length : Math.ceil((tokens.length + 1) / 2)
  return matchedTokensCount >= minimumMatches
}

const getLocalizedPath = (locale, typePath, slug) => {
  if (locale === "es-ES") {
    return `/es-ES${typePath}${slug}`
  }

  return `${typePath}${slug}`
}

const getLocalizedRoutePath = (locale, path) => {
  if (!path) {
    return ""
  }

  if (isExternalUrl(path) || locale !== "es-ES") {
    return path
  }

  if (path.startsWith("/es-ES")) {
    return path
  }

  if (path.startsWith("/")) {
    return `/es-ES${path}`
  }

  return `/es-ES/${path}`
}

const getSuggestionPath = (locale, hit, config) => {
  if (hit.path) {
    return getLocalizedRoutePath(locale, hit.path)
  }

  if (hit.type === QUICK_LINKS_SECTION) {
    return getLocalizedRoutePath(locale, hit.path)
  }

  if (!config || !hit.slug) {
    return ""
  }

  return getLocalizedPath(locale, config.path, hit.slug)
}

const getSectionTitle = (section, formatMessage, locale) => {
  const config = SECTION_CONFIG[section.index]
  if (!config) {
    return section.index
  }

  if (locale === "en-US") {
    return config.sectionLabelEn || section.index
  }

  if (config.sectionLabelId) {
    return formatMessage({ id: config.sectionLabelId, defaultMessage: config.sectionLabelDefault || section.index })
  }

  return config.sectionLabelEn || section.index
}

const renderSuggestion = (hit, locale) => {
  const config = SECTION_CONFIG[hit.type]
  const path = getSuggestionPath(locale, hit, config)

  if (!path) {
    return <span>{hit.title}</span>
  }

  if (isExternalUrl(path)) {
    return (
      <a href={path}>
        {hit.title}
      </a>
    )
  }

  return (
    <Link to={path}>
      {hit.type === "stories" && hit.story_job_title
        ? `${hit.title}, ${hit.story_job_title}`
        : hit.title}
    </Link>
  )
}

const GlobalSearch = ({ onSelect, disableEnterSelect = false }) => {
  const { firebase, profile, schoolMemberships, subdomainConfig } = useContext(FirebaseContext)
  const { locale, formatMessage } = useIntl()
  const [value, setValue] = useState("")
  const [suggestions, setSuggestions] = useState([])
  const debounceTimerRef = useRef(null)

  const { items: workReadinessSearchItems, error: workReadinessSearchError } = useWorkReadinessSearchItems({
    firebase,
    profile,
    schoolMemberships,
    subdomainSchoolId: subdomainConfig?.subdomainSchoolId,
  })

  const stories = useAllStories(locale)
  const careers = useAllCareers(locale)
  const schools = useAllSchools(locale)
  const majors = useAllMajors(locale)
  const companies = useAllCompanies(locale)
  const blogs = useAllBlogs(locale)

  const quickLinksWithSearchText = useMemo(() => {
    return globalSearchQuickLinks.map((link) => {
      const fallbackAliases = link.aliases?.["en-US"] || []
      const localeAliases = link.aliases?.[locale] || fallbackAliases

      return {
        ...link,
        title: formatMessage({ id: link.labelId, defaultMessage: link.defaultLabel }),
        quick_link_aliases_text: localeAliases.join(" "),
        quick_link_category_text: formatMessage({
          id: link.categoryLabelId,
          defaultMessage: link.categoryDefaultLabel,
        }),
      }
    })
  }, [locale, formatMessage])

  const storiesWithSearchText = useMemo(() => {
    return stories.map((story) => ({
      ...story,
      story_keywords_text: Array.isArray(story.story_keywords)
        ? story.story_keywords.filter(Boolean).join(" ")
        : (story.story_keywords || ""),
      story_school_reference_titles_text: Array.isArray(story.story_school_reference_titles)
        ? story.story_school_reference_titles.filter(Boolean).join(" ")
        : (story.story_school_reference_titles || ""),
    }))
  // Datasets come from build-time static GraphQL queries and never change at runtime,
  // but the hooks return a new array reference on every render. Key on locale so this
  // (and the Fuse index built from it) is not rebuilt on every keystroke/re-render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale])

  const schoolsWithSearchText = useMemo(() => {
    return schools.map((school) => ({
      ...school,
      school_keywords_text: Array.isArray(school.school_keywords)
        ? school.school_keywords.filter(Boolean).join(" ")
        : (school.school_keywords || ""),
    }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale])

  const majorsWithSearchText = useMemo(() => {
    return majors
      .filter((major) => Boolean(
        major.major_bachelor
          || major.major_associate
          || major.major_graduate
          || major.major_master
          || major.major_doctorate
      ))
      .map((major) => ({
        ...major,
        major_keywords_text: Array.isArray(major.major_keywords)
          ? major.major_keywords.filter(Boolean).join(" ")
          : (major.major_keywords || ""),
        major_job_categories_text: toCategoryText(major.major_job_categories),
        major_pathways_text: Array.isArray(major.major_pathways_filter)
          ? major.major_pathways_filter.join(" ")
          : (major.major_pathways_filter || ""),
      }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale])

  const companiesWithSearchText = useMemo(() => {
    return companies.map((company) => ({
      ...company,
      company_industry_text: Array.isArray(company.company_industry_filter)
        ? company.company_industry_filter.join(" ")
        : (company.company_industry_filter || ""),
    }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale])

  const careersWithSearchText = useMemo(() => {
    return careers.map((career) => ({
      ...career,
      career_keywords_text: Array.isArray(career.career_keywords)
        ? career.career_keywords.filter(Boolean).join(" ")
        : (career.career_keywords || ""),
      career_job_categories_text: toCategoryText(career.career_job_categories),
      career_pathways_text: Array.isArray(career.career_pathways_filter)
        ? career.career_pathways_filter.join(" ")
        : (career.career_pathways_filter || ""),
    }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale])

  const datasetsBySection = useMemo(() => ({
    stories: storiesWithSearchText,
    careers: careersWithSearchText,
    schools: schoolsWithSearchText,
    majors: majorsWithSearchText,
    companies: companiesWithSearchText,
    blogs,
    [QUICK_LINKS_SECTION]: quickLinksWithSearchText,
    [WORK_READINESS_CONTENT_SECTION]: workReadinessSearchItems,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [
    storiesWithSearchText,
    careersWithSearchText,
    schoolsWithSearchText,
    majorsWithSearchText,
    companiesWithSearchText,
    blogs,
    quickLinksWithSearchText,
    workReadinessSearchItems,
  ])

  const searchBySection = useMemo(() => {
    return Object.entries(datasetsBySection).reduce((acc, [section, items]) => {
      const config = SECTION_CONFIG[section]
      if (!config) {
        return acc
      }

      acc[section] = new Fuse(items, {
        keys: config.keys,
        includeScore: true,
        threshold: 0.35,
        ignoreLocation: true,
        minMatchCharLength: MIN_QUERY_LENGTH,
      })

      return acc
    }, {})
  }, [datasetsBySection])

  const updateSuggestions = (nextQuery) => {
    const normalizedQuery = (nextQuery || "").trim()

    if (normalizedQuery.length < MIN_QUERY_LENGTH) {
      setSuggestions([])
      return
    }

    const sectionResults = Object.entries(searchBySection)
      .map(([section, fuseIndex]) => {
        const config = SECTION_CONFIG[section]
        const fuzzyResults = fuseIndex.search(normalizedQuery)
        const strictTokenResults = fuzzyResults.filter((result) => {
          if (section === "stories") {
            return matchesStoryQueryTokens(result.item, normalizedQuery)
          }

          return matchesQueryTokensForKeys(result.item, normalizedQuery, config?.keys)
        })
        const selectedResults = strictTokenResults.length === 0 ? fuzzyResults : strictTokenResults

        const rankedResults = selectedResults
          .slice()
          .sort((a, b) => {
            const aPriority = getTitleMatchPriority(a.item, normalizedQuery)
            const bPriority = getTitleMatchPriority(b.item, normalizedQuery)
            if (aPriority !== bPriority) {
              return aPriority - bPriority
            }

            const aScore = a.score ?? Number.POSITIVE_INFINITY
            const bScore = b.score ?? Number.POSITIVE_INFINITY
            if (aScore !== bScore) {
              return aScore - bScore
            }

            return String(a.item?.title || "").localeCompare(String(b.item?.title || ""))
          })

        const hits = rankedResults
          .slice(0, SECTION_LIMIT)
          .map((result) => ({
            ...result.item,
            type: section,
            _score: result.score ?? Number.POSITIVE_INFINITY,
            _titleMatchPriority: getTitleMatchPriority(result.item, normalizedQuery),
          }))

        return {
          index: section,
          bestPriority: hits[0]?._titleMatchPriority ?? Number.POSITIVE_INFINITY,
          bestScore: hits[0]?._score ?? Number.POSITIVE_INFINITY,
          hits,
        }
      })
      .filter((sectionResult) => sectionResult.hits.length > 0)
      .sort((a, b) => {
        if (a.bestPriority !== b.bestPriority) {
          return a.bestPriority - b.bestPriority
        }

        if (a.bestScore !== b.bestScore) {
          return a.bestScore - b.bestScore
        }

        return b.hits.length - a.hits.length
      })

    setSuggestions(sectionResults)
  }

  useEffect(() => {
    if (workReadinessSearchError) {
      console.error("Error loading work readiness search items:", workReadinessSearchError)
    }
  }, [workReadinessSearchError])

  const onChange = (event, { newValue }) => {
    setValue(newValue)
  }

  // Desync the expensive multi-section Fuse search from the keystroke that triggers it.
  // The input value updates synchronously in onChange so typing stays responsive, while
  // the suggestion computation is debounced so fast typing doesn't run a search on every
  // character. This mirrors the query desync used on the explore pages, adapted to
  // react-autosuggest's fetch/clear lifecycle so results still render reliably.
  const onSuggestionsFetchRequested = ({ value: nextValue }) => {
    clearTimeout(debounceTimerRef.current)
    debounceTimerRef.current = setTimeout(() => {
      updateSuggestions(nextValue)
    }, SUGGESTION_DEBOUNCE_MS)
  }

  const onSuggestionsClearRequested = () => {
    clearTimeout(debounceTimerRef.current)
    setSuggestions([])
  }

  useEffect(() => {
    return () => clearTimeout(debounceTimerRef.current)
  }, [])

  const getSuggestionValue = (hit) => hit.title || ""

  const onSuggestionSelected = (event, { suggestion, method }) => {
    if (method === "enter" && disableEnterSelect) {
      return
    }

    const config = SECTION_CONFIG[suggestion.type]
    const path = getSuggestionPath(locale, suggestion, config)
    if (!path) {
      return
    }

    if (method === "enter" || method === "click") {
      if (isExternalUrl(path)) {
        window.location.assign(path)
      } else {
        navigate(path)
      }

      if (onSelect) {
        onSelect()
      }
    }

    setValue("")
    setSuggestions([])
  }

  const handleInputKeyDownCapture = (event) => {
    if (disableEnterSelect && event.key === "Enter") {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  const inputProps = {
    placeholder: formatMessage({ id: "global-search-placeholder", defaultMessage: "Search people, careers, majors, schools, tools..." }),
    onChange,
    onKeyDownCapture: handleInputKeyDownCapture,
    value,
  }

  return (
    <AutoSuggest
      suggestions={suggestions}
      multiSection={true}
      onSuggestionsFetchRequested={onSuggestionsFetchRequested}
      onSuggestionsClearRequested={onSuggestionsClearRequested}
      onSuggestionSelected={onSuggestionSelected}
      shouldKeepSuggestionsOnSelect={() => disableEnterSelect}
      getSuggestionValue={getSuggestionValue}
      renderSuggestion={(hit) => renderSuggestion(hit, locale)}
      inputProps={inputProps}
      renderSectionTitle={(section) => getSectionTitle(section, formatMessage, locale)}
      getSectionSuggestions={(section) => section.hits}
    />
  )
}

export default GlobalSearch
